import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import moment from 'moment';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { Paper } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// Services
import { exportSession, getSessions } from '../../../services/scanSession.service';

// Utils
import { useExport } from '../../../hooks/useExport';
import { makeScanSessionListColumn } from '../../../utils/columns/makeColumn';
import { Storage, getRetainState } from '../../../utils/Storage';
import { FormatForApi, shortDateFormat } from '../../../utils/Format';

// Common Components
import { useStandardTable as useTable, Table } from '../../common/table/index';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';
import SearchBar from '../../common/SearchBar/SearchBar';

// Types
import { FilterableTypes } from '../../../types/FilterableTypes';
import { SearchBarFilterTypes } from '../../../constants/Constants';

interface ScanSessionProp {
  accessKey?: string;
}

export default function ScanSessions(props: ScanSessionProp): JSX.Element {
  const navigate = useNavigate();

  const [startDateForm, setStartDateForm] = useState<Date | null>(null);
  const [startDateTo, setStartDateTo] = useState<Date | null>(null);
  const [paramFilter, setParamFilter] = useState<any>({
    entityType: 'ScanSession',
    filters: [],
    sortBy: [{ startDateTime: '-1' }],
  });
  const [isResetting, setIsResetting] = useState(false);

  const { isExporting, startExport } = useExport('ScanSession');

  const intialDefaultFilters = () => {
    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.SCAN_SESSIONS);

    getRetainState(SearchBarFilterTypes.SCAN_SESSIONS, 'startDateTime', setStartDateForm, true, FilterableTypes.GreaterThanOrEqual);
    getRetainState(SearchBarFilterTypes.SCAN_SESSIONS, 'startDateTime', setStartDateTo, true, FilterableTypes.LessThanOrEqual);

    if (filterState) {
      setParamFilter({
        entityType: 'ScanSession',
        filters: filterState.filters,
        sortBy: paramFilter.sortBy,
      });
    }
  };

  useEffect(() => {
    intialDefaultFilters();
  }, []);

  useEffect(() => {
    if (isResetting) {
      Storage.setSearchBarFiltering(SearchBarFilterTypes.SCAN_SESSIONS, { isMoreFilter: false, filters: [] });
      setParamFilter({ entityType: 'ScanSession', filters: [], sortBy: [{ startDateTime: '-1' }] });
      setIsResetting(false);
    }
  }, [isResetting, paramFilter.sortBy]);

  const onInfoClick = (event: any, scanSessionId: string) => {
    if (event.ctrlKey === true) window.open(`/report/${scanSessionId}`);
    else
      navigate(`/report/${scanSessionId}`, {
        state: {
          source: 'scanSessions',
        },
      });
  };

  const columns = useMemo(() => makeScanSessionListColumn(onInfoClick), []);

  const table = useTable<any>({
    columns,
    accessKey: props.accessKey,
    fetchAsync: getSessions,
    exportAsync: exportSession,
    params: paramFilter,
    serverSide: true,
  });

  const onSearchChange = (filters: any) => {
    const finalFilters = [...filters];
    if (startDateForm && startDateTo) {
      finalFilters.push({
        startDateTime: FormatForApi(moment(startDateForm), true),
        filterType: FilterableTypes.GreaterThanOrEqual,
        and: {
          startDateTime: FormatForApi(moment(startDateTo), false),
          filterType: FilterableTypes.LessThanOrEqual,
        },
      });
    }
    setParamFilter({ entityType: 'ScanSession', filters: finalFilters, sortBy: paramFilter.sortBy });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.SCAN_SESSIONS, { isMoreFilter: true, filters: finalFilters });
  };

  const handleReset = () => {
    setIsResetting(true);
    setStartDateForm(null);
    setStartDateTo(null);
    setParamFilter((prev) => ({
      ...prev,
      entityType: 'ScanSession',
      filters: [],
      sortBy: [{ startDateTime: '-1' }],
    }));
    Storage.setSearchBarFiltering(SearchBarFilterTypes.SCAN_SESSIONS, { isMoreFilter: false, filters: [] });
  };

  return (
    <React.Fragment>
      <SearchBar
        pageKey={SearchBarFilterTypes.SCAN_SESSIONS}
        onSearch={onSearchChange}
        onReset={handleReset}
        columns={columns}
        allowSearchAll={true}
        components={
          <div style={{ display: 'flex', marginBottom: 5 }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <FormInputCollection label='Started date'>
                <div style={{ marginRight: '8px' }}>
                  <DatePicker
                    value={startDateForm ? moment(startDateForm) : null}
                    label='From'
                    format={shortDateFormat}
                    sx={{ width: '185px' }}
                    slotProps={{ textField: { size: 'small' } }}
                    onChange={(date) => {
                      setStartDateForm(date ? date.toDate() : null);
                    }}
                  />
                </div>

                <DatePicker
                  value={startDateTo ? moment(startDateTo) : null}
                  label='To'
                  format={shortDateFormat}
                  sx={{ width: '185px' }}
                  slotProps={{ textField: { size: 'small' } }}
                  onChange={(date) => {
                    setStartDateTo(date ? date.toDate() : null);
                  }}
                />
              </FormInputCollection>
            </LocalizationProvider>
          </div>
        }
      />
      <Paper>
        <Table
          {...table}
          isExporting={isExporting}
          canExportData={() => true}
          exportData={() => startExport(paramFilter.filters ?? [])}
          showSearchBar={false}
          showColumnToggler={false}
          showAction={true}
          getCoreRowModel={getCoreRowModel()}
        />
      </Paper>
    </React.Fragment>
  );
}
